// src/pages/Platform/PlatformSettings.js
//
// Grace days / post-grace action / reminder lead days / SMTP — handlers.
// GetPlatformSettingsHandler/UpdatePlatformSettingsHandler, task #202. A
// single "Save" button submits both categories in one request, same shape
// UpdateConfigSettings' own tenant-facing Config screen already uses.
import { useState, useEffect, useContext } from "react";
import { getPlatformSettings, updatePlatformSettings } from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";

const MASK = "••••••••";

export default function PlatformSettings() {
  const { token } = useContext(PlatformAuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const [gracePeriodDays, setGracePeriodDays] = useState(7);
  const [postGraceAction, setPostGraceAction] = useState("suspend");
  const [reminderLeadDays, setReminderLeadDays] = useState("14,7,3,1");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await getPlatformSettings(token);
        setGracePeriodDays(res.subscription.grace_period_days);
        setPostGraceAction(res.subscription.post_grace_action);
        setReminderLeadDays((res.subscription.reminder_lead_days || []).join(","));
        setSmtpHost(res.smtp.host);
        setSmtpPort(res.smtp.port);
        setSmtpUsername(res.smtp.username);
        setSmtpPassword(res.smtp.password); // either "" or the MASK sentinel
        setSmtpFrom(res.smtp.from_address);
      } catch (err) {
        setError(err.message || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaved(false);
    setSaving(true);
    try {
      const leadDays = reminderLeadDays
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n));

      await updatePlatformSettings(token, {
        subscription: {
          grace_period_days: Number(gracePeriodDays),
          post_grace_action: postGraceAction,
          reminder_lead_days: leadDays,
        },
        smtp: {
          host: smtpHost,
          port: Number(smtpPort),
          username: smtpUsername,
          password: smtpPassword, // sending back the mask sentinel is a no-op server-side
          from_address: smtpFrom,
        },
      });
      setSaved(true);
    } catch (err) {
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 520 }}>
      <h2 style={{ margin: "0 0 20px", fontSize: 20, color: "#0f172a" }}>Platform settings</h2>

      <form onSubmit={handleSubmit}>
        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Subscription</h3>

          <label style={labelStyle}>Grace period (days)</label>
          <input
            style={inputStyle}
            type="number"
            min="0"
            value={gracePeriodDays}
            onChange={(e) => setGracePeriodDays(e.target.value)}
          />

          <label style={labelStyle}>When the grace period ends</label>
          <select style={inputStyle} value={postGraceAction} onChange={(e) => setPostGraceAction(e.target.value)}>
            <option value="suspend">Suspend the company</option>
            <option value="keep_running">Keep it running until I decide</option>
          </select>

          <label style={labelStyle}>Reminder lead days (comma-separated)</label>
          <input
            style={inputStyle}
            value={reminderLeadDays}
            onChange={(e) => setReminderLeadDays(e.target.value)}
            placeholder="14,7,3,1"
          />
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Outgoing email (SMTP)</h3>
          <p style={{ fontSize: 13, color: "#64748b", marginTop: -4 }}>
            Used for renewal reminders and new-tenant welcome emails. Leave the host blank to disable email sending.
          </p>

          <label style={labelStyle}>Host</label>
          <input style={inputStyle} value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="smtp.example.com" />

          <label style={labelStyle}>Port</label>
          <input style={inputStyle} type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} />

          <label style={labelStyle}>Username</label>
          <input style={inputStyle} value={smtpUsername} onChange={(e) => setSmtpUsername(e.target.value)} />

          <label style={labelStyle}>Password</label>
          <input
            style={inputStyle}
            type="password"
            value={smtpPassword}
            onFocus={() => { if (smtpPassword === MASK) setSmtpPassword(""); }}
            onChange={(e) => setSmtpPassword(e.target.value)}
            placeholder={smtpPassword === MASK ? "Unchanged" : ""}
          />

          <label style={labelStyle}>From address</label>
          <input style={inputStyle} type="email" value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)} />
        </section>

        {error && <div style={errorBoxStyle}>{error}</div>}
        {saved && <div style={successBoxStyle}>Settings saved.</div>}

        <button type="submit" style={primaryBtnStyle} disabled={saving}>
          {saving ? "Saving..." : "Save settings"}
        </button>
      </form>
    </div>
  );
}

const sectionStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, marginBottom: 16 };
const sectionTitleStyle = { margin: "0 0 12px", fontSize: 14, color: "#0f172a" };
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box", marginBottom: 4 };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "12px 0 4px" };
const primaryBtnStyle = { background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const errorBoxStyle = { background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 };
const successBoxStyle = { background: "#dcfce7", color: "#166534", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 };
